import { useEffect, useRef, useState } from 'react';
import {
  Button,
  ContentLayout,
  Form,
  Link,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import { useIntl } from 'react-intl';

import { PostAuthComponentParams } from '../../types/auth';
import metricsService from '../../services/metrics';
import CardContainer from '../CardContainer';
import { IMPRESSIONS } from '../../constants/metrics';
import { isEulaAccepted, acceptEula } from '../../services/eula';
import eulaMessages from '../../i18n/eula.messages';
import { T_AND_C_PRIVACY, T_AND_C_TERMS } from '../../constants/links';
import { rumService } from '../../services/rum';
import { RUM_EVENTS } from '../../constants/metrics';
import { shouldShowSimplifiedSignIn } from '../../utils/featureEvaluator';
import { isEltiProvider, IDP_ESSO } from '../../constants/providers';
import logger from '../../utils/logger';

/**
 * Returns true if the user should have EULA auto-accepted (simplified flow,
 * non-ESSO, non-ELTI). Mirrors the backend skip logic in
 * claim_generator_supergraph_edits.go.
 *
 * Requires enableIdpTypeCheck='true' so that idpDetails is populated in
 * AuthResponse.tsx before EulaCheck renders. Without it, idpType is
 * undefined and auto-accept safely falls back to showing the EULA screen.
 */
export function shouldAutoAcceptEula(
  config: PostAuthComponentParams['config'],
  idpType?: string
): boolean {
  if (!shouldShowSimplifiedSignIn(config)) return false;
  if (!idpType) {
    logger.info(
      'EULA auto-accept skipped: idpType unknown (enableIdpTypeCheck may be off)'
    );
    return false;
  }
  if (isEltiProvider(idpType)) return false;
  if (idpType === IDP_ESSO) return false;
  return true;
}

const EulaCheck = ({
  user,
  onContinue,
  onCancel,
  config,
  consentAccepted,
  idpDetails,
}: PostAuthComponentParams) => {
  const { formatMessage } = useIntl();
  const [showEula, setShowEula] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const metricsPublisher = useRef(metricsService.getPublisher('EULACheck'));
  const hasAttemptedAutoAccept = useRef(false);
  useEffect(() => {
    if (!showEula) return;
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
    rumService.recordEvent(RUM_EVENTS.EULA_IMPRESSION, {});
  }, [showEula]);

  const enableEulaPostAuthFlow = config.enableEulaPostAuthFlow;
  const idpType = idpDetails?.idpType;

  useEffect(() => {
    if (enableEulaPostAuthFlow !== 'true') {
      setShowEula(false);
      onContinue();
      return;
    }
    if (isEulaAccepted(user, consentAccepted)) {
      onContinue();
      return;
    }

    // Auto-accept for simplified login flow (non-ESSO, non-ELTI).
    // The consent text is already shown inline on the simplified sign-in page.
    if (shouldAutoAcceptEula(config, idpType)) {
      if (hasAttemptedAutoAccept.current) return;
      hasAttemptedAutoAccept.current = true;

      acceptEula(config, user)
        .then(() => {
          metricsPublisher.current.publishCounterMonitor('AutoAccept', 1);
          rumService.recordEvent(RUM_EVENTS.EULA_AUTO_ACCEPT, {
            idpType: idpType ?? 'unknown',
          });
          onContinue();
        })
        .catch((error) => {
          // Graceful degradation: fall back to showing the EULA screen
          logger.error('EULA auto-accept failed, falling back to UI', error);
          rumService.recordEvent(RUM_EVENTS.EULA_AUTO_ACCEPT_ERROR, {
            error: error instanceof Error ? error.message : String(error),
          });
          setShowEula(true);
        });
      return;
    }

    setShowEula(true);
  }, [
    onContinue,
    user,
    enableEulaPostAuthFlow,
    consentAccepted,
    idpType,
    config,
  ]);

  const handleAccept = async () => {
    setHasFormError(false);
    setIsLoading(true);
    try {
      await acceptEula(config, user);
      metricsPublisher.current.publishCounterMonitor('Accept', 1);
      rumService.recordEvent(RUM_EVENTS.EULA_ACCEPT, {});
      onContinue();
    } catch (error) {
      metricsPublisher.current.publishCounterMonitor('Error', 1);
      rumService.recordEvent(RUM_EVENTS.EULA_ERROR, {
        error: error instanceof Error ? error.message : String(error),
      });
      setHasFormError(true);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    metricsPublisher.current.publishCounterMonitor('Cancel', 1);
    rumService.recordEvent(RUM_EVENTS.EULA_CANCEL, {});
    onCancel();
  };

  if (!showEula) return null;

  return (
    <ContentLayout>
      <CardContainer header={formatMessage(eulaMessages.header)}>
        <Form
          errorText={
            hasFormError && formatMessage(eulaMessages.eulaUpdateError)
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => handleCancel()}
                disabled={isLoading}
                data-testid="eula-cancel"
              >
                {formatMessage(eulaMessages.cancelButton)}
              </Button>
              <Button
                variant="primary"
                onClick={handleAccept}
                loading={isLoading}
                data-testid="eula-accept"
              >
                {formatMessage(eulaMessages.acceptButton)}
              </Button>
            </SpaceBetween>
          }
        >
          <TextContent>
            <p data-testid="eula-content">
              {formatMessage(eulaMessages.eulaInformation, {
                termsLink: (chunks) => (
                  <Link
                    href={T_AND_C_TERMS}
                    target="_blank"
                    rel="noopener noreferrer"
                    ariaLabel={formatMessage(
                      eulaMessages.termsAndConditionsAndOrganizationAriaLabel
                    )}
                  >
                    {chunks}
                  </Link>
                ),
                privacyLink: (chunks) => (
                  <Link
                    href={T_AND_C_PRIVACY}
                    target="_blank"
                    rel="noopener noreferrer"
                    ariaLabel={formatMessage(
                      eulaMessages.privacyNoticeAndOrganizationAriaLabel
                    )}
                  >
                    {chunks}
                  </Link>
                ),
              })}
            </p>
          </TextContent>
        </Form>
      </CardContainer>
    </ContentLayout>
  );
};

export default EulaCheck;
